{
  "title": "Servers",
  "description": "Subscription status, version and last check-in for each server.",
  "detail": {
    "title": "Server details",
    "description": "Subscription, activation key, install and updates for this server.",
    "tabsLabel": "Server sections"
  },
  "add": {
    "title": "Add a server",
    "description": "Choose a plan, then install bPanel on your server with one command."
  },
  "actions": {
    "add": "Add server",
    "activateCode": "Activate with a code",
    "openPanel": "Open panel"
  },
  "toasts": {
    "saved": "Changes saved.",
    "removed": "Server removed.",
    "reissued": "New activation key issued.",
    "scheduledDropped": "Your current plan stays as it is.",
    "cancelled": "Subscription cancelled. It stays active until the end of the period, then the control panel locks.",
    "resumed": "Automatic renewal is back on."
  },
  "tabs": {
    "overview": "Overview",
    "activation": "Activation",
    "plan": "Plan",
    "ai": "AI credits",
    "updates": "Updates",
    "settings": "Settings"
  },
  "list": {
    "search": "Search by name, hostname or IP",
    "columns": {
      "server": "Server",
      "state": "Subscription status",
      "plan": "Plan",
      "accounts": "Hosting accounts",
      "version": "Version",
      "checkIn": "Last check-in"
    },
    "filters": {
      "state": "Status",
      "plan": "Plan"
    },
    "endsOn": "Ends {{date}}",
    "graceUntil": "Offline; grace until {{date}}",
    "installing": "Installing",
    "capacityOnly": "Up to {{n}} accounts",
    "update": "Update",
    "updateTo": "Version {{version}} is available",
    "notInstalled": "Not installed yet",
    "never": "Not checked in yet",
    "actions": {
      "view": "View server",
      "install": "Install steps",
      "copyHostname": "Copy hostname",
      "renew": "Renew now"
    },
    "empty": {
      "title": "No servers yet",
      "body": "Add your first server and install bPanel with one command. The free trial is 7 days, with nothing to pay."
    },
    "locked": "Locked; renew to open the panel",
    "unlocking": "Renewed; unlocking shortly",
    "payBy": "Pay by {{date}}"
  },
  "fields": {
    "name": "Server name",
    "nameHint": "A name that helps you tell it apart, like Main server.",
    "hostname": "Hostname",
    "hostnameHint": "The full name that points to the server's IP address, like server1.example.sa. We use it for the panel link and its SSL certificate.",
    "server": "Server",
    "plan": "Plan",
    "capacity": "Capacity",
    "state": "Subscription status",
    "ip": "IP address",
    "os": "Operating system",
    "version": "bPanel version",
    "installationId": "Installation ID",
    "lastCheckIn": "Last check-in",
    "activatedAt": "Activated",
    "createdAt": "Added",
    "notReported": "Shown after activation",
    "notActivated": "Not activated yet"
  },
  "state": {
    "pending_activation": {
      "title": "Waiting for install and activation",
      "body": "Install bPanel on the server with the activation key. The subscription activates on its own as soon as the server reaches us."
    },
    "transferring": {
      "title": "Waiting for the new server",
      "body": "The subscription is paid and stays as it is. Install bPanel on {{host}} with the new activation key, and it activates as soon as the server connects to us."
    },
    "trial": {
      "title": "On the free trial until {{date}}",
      "body": "Every bPanel feature is available on this server. Set up payment before the trial ends to keep it going without a break; otherwise the control panel locks when it ends."
    },
    "valid": {
      "title": "Everything is running",
      "body": "The subscription is active and renews automatically on {{date}}.",
      "bodyNoDate": "The subscription is active and renews automatically."
    },
    "grace": {
      "title": "The server can't reach bPanel; grace until {{date}}",
      "body": "The server couldn't refresh its licence because it can't reach bPanel. The subscription is paid, and websites, mail and databases run as usual. If it doesn't connect by {{date}}, adding new hosting accounts stops until it does."
    },
    "degraded": {
      "title": "The server has been offline for a while",
      "body": "The offline grace is over. Websites keep running and you can still sign in and export your data, but new accounts and upgrades are paused until the server reaches bPanel."
    },
    "expired": {
      "title": "The licence on the server has expired",
      "body": "The server hasn't reached bPanel for a long time. Websites keep running and your data stays exportable. Check the server's connection, and contact support if you need help."
    },
    "ending": {
      "title": "Subscription ends on {{date}}",
      "body": "You cancelled the renewal. The server runs as usual until {{date}}, when the control panel locks until you renew. Websites, mail, databases and DNS keep running, and nothing is deleted."
    },
    "keeps": {
      "title": "Keeps running",
      "lock": {
        "services": "Websites, mail and databases",
        "dns": "DNS keeps answering as usual",
        "data": "No account, file or backup is deleted"
      },
      "offline": {
        "sites": "Websites, mail and databases",
        "access": "Sign-in for you and your customers",
        "export": "Data export and backup downloads"
      }
    },
    "stops": {
      "title": {
        "lock": "Stops until you renew",
        "offline": "Stops after the offline grace"
      },
      "lock": {
        "panel": "The Provider Console, except renewal and licensing",
        "customers": "Panel access for hosting customers, who see a notice instead",
        "ai": "AI features, and updates other than security ones"
      },
      "offline": {
        "accounts": "Adding new hosting accounts",
        "upgrades": "Hosting package upgrades and advanced operations",
        "updates": "Updates, except security ones"
      }
    },
    "past_due": {
      "title": "The renewal payment didn't go through; pay by {{date}}",
      "body": "The server and its control panel run normally through the payment grace. If it isn't paid by {{date}}, the subscription ends and the control panel locks until you renew; websites and mail keep running."
    },
    "locked": {
      "title": "The control panel is locked until you renew",
      "body": "The Provider Console shows only renewal and licensing, and hosting customers see a notice that their provider manages the panel. After you renew, it unlocks at the server's next check-in with bPanel."
    },
    "unlocking": {
      "title": "Renewed; the panel unlocks shortly",
      "body": "We have your payment. The control panel unlocks at the server's next check-in with bPanel, usually within minutes."
    }
  },
  "overview": {
    "installCommands": "Show install commands",
    "facts": "Server details",
    "capacity": "Capacity",
    "resellers": "Resellers",
    "hostExtra": "Host includes {{n}} accounts, and each extra account is added to the renewal.",
    "nearFull": "This server is close to its plan's capacity. Choose a bigger plan before you need it.",
    "capacityNote": "Active and suspended accounts count; terminated accounts don't.",
    "perAccountMonth": "per account a month",
    "changePlan": "Change plan",
    "renewal": "Subscription",
    "endsOn": "Ends on",
    "trialEnds": "Trial ends",
    "firstCharge": "First charge",
    "renewsOn": "Next renewal",
    "onActivation": "On activation",
    "amount": "Amount",
    "exclVat": "Excluding VAT",
    "billing": "Billing",
    "panel": "bPanel panel",
    "panelBody": "The panel link on this server.",
    "endedOn": "Ended on",
    "beforeInstall": "Before install"
  },
  "activation": {
    "installation": "Installation",
    "checkInHint": "The server checks in at least once every 24 hours.",
    "history": "Activation history",
    "historyBody": "Everything that happened to the activation key and this installation.",
    "installAgain": "Install commands",
    "installPending": "Install bPanel on the server with these commands.",
    "installAgainBody": "To reinstall on the same server."
  },
  "key": {
    "description": "Links the installation to your subscription. Don't share it outside your team.",
    "noAccess": "The owner, admins and technical members can see the key.",
    "reissue": "Issue a new key",
    "rotation": {
      "pendingTitle": "Waiting for the server to switch to the new key",
      "pendingBody": "The server picks up the new key by itself at its next check-in, then the old key stops working. Nothing to do on your side.",
      "waiting": "Waiting for the server to check in and switch to the new key",
      "applied": "Key last issued on {{date}}"
    },
    "confirm": {
      "title": "Issue a new activation key?",
      "body": "We'll issue a new key for {{host}} and retire the current one.",
      "action": "Issue new key",
      "running": "The server keeps running and switches to the new key at its next check-in. After that, the old key stops working.",
      "pending": "The old key stops working right away. Use the new key in the install command.",
      "others": "Any other installation that used the old key stops getting updates.",
      "sites": "Websites on the server aren't affected."
    }
  },
  "telemetry": {
    "title": "What bPanel sends us",
    "description": "The server sends the minimum we need to check the subscription, about once every 24 hours.",
    "sendsTitle": "Sends",
    "sends": {
      "installationId": {
        "label": "Installation ID",
        "body": "an ID bPanel creates the first time it runs"
      },
      "publicKey": {
        "label": "Public key",
        "body": "to verify requests come from the same server"
      },
      "signals": {
        "label": "Server signals",
        "body": "hashes of the machine ID and hostname, and the public IP address"
      },
      "versions": {
        "label": "Versions",
        "body": "the versions of bPanel and its components"
      },
      "plan": {
        "label": "Plan",
        "body": "the plan active on the server"
      },
      "counts": {
        "label": "Counts",
        "body": "only the number of hosting accounts and resellers"
      },
      "features": {
        "label": "Features in use",
        "body": "whether a feature is used or not, no details"
      }
    },
    "neverTitle": "Never sends",
    "never": {
      "domains": "Domain names",
      "customers": "Your customers' data or identities",
      "emails": "Email addresses or messages",
      "usage": "Usage per hosting account",
      "logs": "Logs"
    },
    "footnote": "Your customers' browsers never contact bPanel. Only the server talks to us."
  },
  "plan": {
    "current": "Current plan",
    "currentBody": "{{plan}}",
    "price": "Price",
    "scheduled": "The plan changes to {{plan}} on {{date}}.",
    "keepCurrent": "Keep current plan",
    "lockedTitle": "Plan changes aren't available",
    "lockedBody": "You cancelled this server's renewal. Undo the cancellation in settings to change the plan.",
    "noAccess": "The owner, admins and billing members can change the plan.",
    "change": "Change plan",
    "hostNote": "Host includes {{n}} accounts. Each extra account is",
    "toast": {
      "changed": "Your plan is now {{plan}}.",
      "scheduled": "The plan changes on {{date}}."
    },
    "preview": {
      "title": "Summary",
      "pick": "Pick a different plan or billing cycle to see the price.",
      "applyNow": "Change plan now",
      "schedule": "Schedule change",
      "perMonth": "a month",
      "perYear": "a year",
      "trial": "Free during the trial, and the change applies right away.",
      "notActivated": "The server isn't activated yet, so the change is free, applies now, and uses today's price for the new plan.",
      "now": "Applies now; you pay the difference for the days left in the current period.",
      "later": "The change applies on {{date}}, at the end of the current period. Nothing to pay now.",
      "newPrice": "{{plan}} price",
      "credit": "Credit for unused time",
      "dueNow": "Due now",
      "vat": "VAT {{rate}}",
      "noVat": "VAT",
      "total": "Total now",
      "nextRenewal": "Renews on {{date}}",
      "prorationNote": "We charge the price difference only for the rest of the current period. Prices exclude VAT unless shown.",
      "noRefundNote": "You keep your current plan until the end of the period; the current period isn't refunded.",
      "vatNote": "Prices exclude VAT.",
      "continueToPay": "Continue to payment"
    },
    "blocked": {
      "title": "You can't move to this plan yet",
      "body": "The server has {{used}} hosting accounts and {{plan}} allows {{capacity}}. Remove or move some accounts first, or choose a bigger plan."
    },
    "confirm": {
      "title": "Change to {{plan}}?",
      "later": "The change applies on {{date}} and nothing is charged now.",
      "free": "The change is free and applies right away."
    },
    "pastDue": {
      "title": "Pay the open invoice first",
      "body": "The renewal payment didn't go through. You can change the plan once it's paid."
    },
    "yourPriceNote": "Your price stays with every renewal while you keep the same plan and billing cycle. Any change moves you to today's price.",
    "lockedOut": {
      "title": "The control panel is locked",
      "body": "The subscription has ended. Renew on the same plan at your price, or pick another plan at today's price when you renew.",
      "renewed": "Renewed. The panel unlocks at the server's next check-in; after that you can change the plan."
    }
  },
  "ai": {
    "title": "AI credits this month",
    "renews": "Included credits renew on {{date}}.",
    "left": "Left",
    "summary": "{{plan}} includes {{monthly}} credits a month for this server, and you have {{left}} left.",
    "empty": "This month's credits are used up. Buy extra credits or wait for the renewal.",
    "manage": "Buy credits and see usage",
    "packs": "Every {{n}} extra credits for",
    "byPlan": "Monthly credits per plan",
    "byPlanNote": "Credits are per server and renew at the start of each month."
  },
  "updates": {
    "version": "Version",
    "running": "On the server",
    "latest": "Latest ({{channel}})",
    "notInstalled": "The version shows once bPanel is installed.",
    "available": "Version {{version}} is available",
    "availableShort": "{{version}} available",
    "howTo": "bPanel updates from the server itself: from the Updates page in your panel, or with the command below.",
    "securityOnly": "The control panel is locked or the server is offline, so it gets security updates only.",
    "upToDate": "The server is on the latest version.",
    "changelog": "Changelog",
    "channel": "Update channel",
    "channelBody": "Which releases reach this server.",
    "stableBody": "Tested releases. Recommended for customer servers.",
    "betaBody": "New features first. Good for test servers.",
    "noAccess": "The owner, admins and technical members can change the channel.",
    "betaNote": "On beta you get releases before they're final. Switch back to stable any time; you'll get the first stable release newer than yours."
  },
  "settings": {
    "name": {
      "title": "Server name",
      "body": "Only you and your team see it."
    },
    "techOnly": "Available to the owner, admins and technical members.",
    "ownerOnly": "Available to the owner and admins.",
    "billingOnly": "Available to the owner, admins and billing members."
  },
  "transfer": {
    "title": "Transfer to a new server",
    "body": "Changing hardware or provider? Move this bPanel subscription to the new server without a second subscription.",
    "points": {
      "key": "We issue a new activation key for the new server.",
      "old": "The old installation stops at its next check-in; websites on it keep running.",
      "billing": "Plan, billing and renewal date stay the same.",
      "limit": "You can transfer a server once every 30 days."
    },
    "start": "Start transfer",
    "steps": {
      "why": "Reason",
      "where": "New server",
      "confirm": "Confirm",
      "done": "Done"
    },
    "why": "Why are you moving the server?",
    "note": "Note for our support team",
    "reasons": {
      "new_hardware": {
        "label": "New server at the same provider",
        "body": "An upgrade or a hardware swap."
      },
      "new_provider": {
        "label": "New provider",
        "body": "Moving to another data centre or provider."
      },
      "reinstall": {
        "label": "Reinstalling the operating system",
        "body": "Same server, fresh system."
      },
      "other": {
        "label": "Something else",
        "body": "Tell us in the note."
      }
    },
    "hostname": "New server's hostname",
    "hostnameHint": "Leave it as is if the new server keeps the same name.",
    "confirm": "Transfer and issue key",
    "confirmPoints": {
      "key": "We issue a new activation key. Use it to install bPanel on the new server.",
      "old": "The installation on {{host}} stops at its next check-in, so you can't add accounts there.",
      "sites": "Websites on the old server keep running. Move them to the new one with bPanel's migration tool.",
      "billing": "Your subscription and renewal date don't change."
    },
    "installNow": "Install on the new server",
    "doneTitle": "Transfer started",
    "doneBody": "Install bPanel on {{host}} with the new key. It activates as soon as it connects to us.",
    "notice": {
      "title": "Transfer to a new server in progress",
      "action": "Install steps",
      "oldWaiting": "Install bPanel on the new server with the new key. The old installation on {{host}} stops at its next check-in.",
      "oldStopped": "The old installation on {{host}} stopped {{time}}. Install bPanel on the new server with the new key."
    }
  },
  "remove": {
    "title": "Remove server",
    "body": "The server isn't installed yet. If you haven't paid its first period, you can remove it at no cost; if you have, cancel the subscription instead.",
    "action": "Remove server",
    "confirmTitle": "Remove this server?",
    "confirmBody": "We'll delete the server and its activation key. This can't be undone."
  },
  "cancel": {
    "title": "Cancel subscription",
    "body": "Renewal stops, and the server keeps running until the end of the paid period. Then the control panel locks; websites and mail keep running.",
    "action": "Cancel subscription",
    "timeline": {
      "now": "Now",
      "nowBody": "Automatic renewal stops. There's no cancellation fee, and the time left in the period isn't refunded.",
      "endBody": "End of the period. The subscription ends and the control panel locks until you renew. There is no grace after cancelling.",
      "always": "Always",
      "alwaysBody": "Websites, mail, databases and DNS never stop, and nothing is deleted. Renew any time to unlock the panel."
    },
    "confirmTitle": "Cancel this server's subscription?",
    "confirmBody": "The subscription stays active until {{date}}. Cancelling doesn't refund the rest of the paid period. You can undo this before then.",
    "confirm": "Cancel subscription",
    "keep": "Keep subscription",
    "reason": "Why are you cancelling?",
    "reasons": {
      "not_needed": "I don't need this server anymore",
      "too_expensive": "It costs more than my budget",
      "moving_away": "I'm moving to another control panel",
      "missing_feature": "A feature I need is missing",
      "other": "Something else"
    },
    "feedback": "What could we do better?",
    "export": "You can export your data from the bPanel panel on the server until the period ends. After it locks, renew to open the panel again.",
    "scheduledTitle": "Subscription ends on {{date}}",
    "scheduledBody": "You turned off renewal. Undo it before that date and the subscription carries on unchanged.",
    "resume": "Keep subscription"
  },
  "install": {
    "req": {
      "title": "Before you start",
      "description": "Make sure your server meets these requirements.",
      "os": {
        "title": "Debian 12, Ubuntu 22.04 or 24.04",
        "body": "An amd64 or arm64 processor, and root access over SSH."
      },
      "fresh": {
        "title": "A fresh server",
        "body": "No other control panel and no web server already set up."
      },
      "size": {
        "title": "At least 1 vCPU and 1 GB of memory",
        "body": "And 20 GB of disk. We recommend 2 GB of memory or more for mail and many sites."
      },
      "hostname": {
        "title": "A full hostname",
        "body": "Like server1.example.sa, pointing to the server's IP for the SSL certificate."
      },
      "ports": {
        "title": "Free ports",
        "body": "80, 443 and {{port}} for the panel, 25, 465, 587, 110, 143, 993 and 995 for mail, and 53 (TCP and UDP) for DNS. Nothing else may be using them."
      }
    },
    "title": "Install bPanel",
    "description": "Run these commands on {{host}} as a user with sudo.",
    "step1": {
      "title": "Add the bPanel repository and install the installer",
      "body": "The same commands on Debian 12 and Ubuntu 22.04 and 24.04: they add the signing key and the repository from apt.bpanel.dev, then install the bpanel-installer package."
    },
    "step2": {
      "title": "Run the installer with your activation key",
      "body": "The installer sets up web, mail, DNS and the panel's SSL certificate, then activates the subscription.",
      "hint": "The install usually takes 5 to 10 minutes on a fresh server. If it stops at a step, fix the cause and run the same command again; it picks up at that step.",
      "noAccess": "Only the owner, admins and technical members can see the activation key. Ask one of them to finish the install."
    },
    "step3": {
      "title": "Follow the activation here",
      "body": "This page updates by itself at every step, and the subscription activates as soon as the server connects to us.",
      "adminLink": "When it finishes, the server prints a one-time sign-in link for the first admin, valid for {{hours}} hours. The panel is at {{url}}."
    },
    "device": {
      "title": "Or activate with a code instead of the key",
      "body": "Run the installer with this command and it shows an 8-character code. Open {{url}} and enter the code to link the server to your account.",
      "link": "Activate with a code"
    }
  },
  "waiting": {
    "title": "Waiting for the installer",
    "body": "Run the install commands on {{host}}. As soon as the installer starts, its steps show up here.",
    "runningTitle": "Installing",
    "runningBody": "The installer is running on {{os}} ({{arch}}), activating {{method}}.",
    "method": {
      "key": "with the activation key",
      "device_code": "with a device code"
    },
    "stepsLabel": "Install steps",
    "steps": {
      "preflight": "Checking the server",
      "repository": "Adding the bPanel repository",
      "packages": "Installing packages",
      "services": "Setting up web, mail and DNS",
      "tls": "SSL certificate for the panel",
      "activate": "Activating the subscription"
    },
    "stepStatus": {
      "done": "done",
      "running": "in progress",
      "pending": "waiting",
      "failed": "failed"
    },
    "progress": "{{done}} of {{total}} steps",
    "keepOpen": "You can close this page; the install carries on.",
    "failedTitle": "The install stopped",
    "failedBody": "The installer stopped at \"{{step}}\". Fix the cause and run the same command again on the server; it picks up at this step.",
    "failureCode": "Error code {{code}}",
    "failure": {
      "3": "Run the installer as root or with sudo.",
      "10": "This operating system isn't supported. Use Debian 12, Ubuntu 22.04 or 24.04.",
      "11": "This processor isn't supported. bPanel needs amd64 or arm64.",
      "12": "The server isn't fresh: a control panel or web server is installed. Reinstall the OS and try again.",
      "13": "The server is below the minimum: 1 vCPU, 1 GB of memory and 20 GB of disk.",
      "14": "The hostname isn't a full name. Use one like server1.example.sa.",
      "15": "The server can't reach apt.bpanel.dev. Check its network and DNS.",
      "16": "The server's clock is off. Turn on time sync and try again.",
      "17": "A required port is in use. Stop the program using it and try again.",
      "20": "We couldn't add the bPanel repository. Check the connection to apt.bpanel.dev.",
      "21": "Installing the packages failed. Run the command again in a few minutes.",
      "22": "Setting up web, mail and DNS failed.",
      "23": "The bPanel services didn't start. Run the command again, and contact support if it happens again.",
      "24": "We couldn't set up the panel's SSL certificate. Make sure the hostname points to the server.",
      "25": "Activation failed. Check the activation key, or reissue it.",
      "26": "The activation code expired. Run the command again for a new code.",
      "other": "Check the install log at /var/log/bpanel/install.log."
    },
    "runs_one": "First run",
    "runs_other": "Run {{count}}",
    "installerVersion": "Installer {{version}}"
  },
  "done": {
    "title": "Your server is running",
    "body": "bPanel is activated on {{host}} and the subscription is active.",
    "bodyTrial": "bPanel is activated on {{host}} on the free trial.",
    "panelUrl": "Panel link",
    "signIn": "Sign in with the one-time link the installer printed at the end. It works once, within 72 hours.",
    "nextTitle": "Next steps",
    "next": {
      "package": {
        "title": "Create a hosting package and your first account",
        "body": "In bPanel on the server, or move your sites from cPanel with the migration tool."
      },
      "card": {
        "title": "Set up payment",
        "body": "So the server keeps going after the trial and its control panel doesn't lock."
      },
      "team": {
        "title": "Invite your team",
        "body": "Add teammates with the right role: admin, technical or billing."
      }
    }
  },
  "demo": {
    "installTitle": "Demo data: simulate the installer",
    "installBody": "The demo has no real server. Run the simulation to see the steps as they show during an install.",
    "installAuto_one": "The simulation starts by itself in {{count}} second.",
    "installAuto_other": "The simulation starts by itself in {{count}} seconds.",
    "installAction": "I ran the command, start the simulation",
    "installFailAction": "Simulate a certificate error",
    "rerunBody": "The simulation stopped at the certificate. On a real server you fix the cause and run the same command; it picks up where it stopped.",
    "rerunAction": "Run the command again"
  },
  "setup": {
    "defaultName": "Main server",
    "stepOf": "Step {{n}} of {{total}}: {{title}}",
    "steps": {
      "details": "Server",
      "plan": "Plan",
      "payment": "Payment",
      "install": "Install",
      "waiting": "Activation",
      "done": "Done"
    },
    "welcome": {
      "title": "Welcome {{name}}, let's set up your first server",
      "body": "Name the server and choose a plan for the trial, then install bPanel with one command. The trial is 7 days, no card needed."
    },
    "details": {
      "title": "Server details",
      "body": "You can change the name later."
    },
    "plan": {
      "title": "Choose a plan",
      "bodyTrial": "Every plan has every bPanel feature; they differ in hosting accounts and AI credits. The trial is {{days}} days free on any plan, and you can change it any time.",
      "bodyPaid": "Every plan has every bPanel feature; they differ in hosting accounts and AI credits. You can change the plan any time.",
      "create": "Create server and continue"
    },
    "payment": {
      "title": "Payment",
      "perMonth": "Price per month",
      "perYear": "Price per year",
      "vat": "Plus VAT {{rate}}",
      "noVat": "VAT depends on your country and shows on the invoice",
      "inTrialTitle": "Included in your free trial",
      "inTrialBody": "This server is free until your trial ends on {{date}}. Nothing is charged now.",
      "newTrialTitle": "Starts your {{days}}-day free trial",
      "newTrialBody": "Nothing to pay today. Set up payment before the trial ends to keep the server going; otherwise the control panel locks when it ends.",
      "chargeTitle": "You pay the first period before the install",
      "chargeBody_monthly": "After creating the server, you pay the first month, then continue the install. It renews every month at your price.",
      "chargeBody_yearly": "After creating the server, you pay the first year, then continue the install. It renews every year at your price.",
      "create": "Create server and continue",
      "createAndPay": "Create server and continue to payment"
    },
    "install": {
      "startedTitle": "The installer has started on the server.",
      "follow": "Follow progress",
      "ran": "I ran the commands"
    },
    "waiting": {
      "showCommands": "Show the commands again"
    },
    "done": {
      "another": "Add another server",
      "openServer": "View server",
      "overview": "Go to overview"
    }
  },
  "activity": {
    "by": "by {{name}}",
    "methods": {
      "key": "with the activation key",
      "device_code": "with a device code"
    },
    "kinds": {
      "server_created": "Added {{server}} on {{plan}}",
      "install_started": "Install started on {{server}} ({{os}}, {{method}})",
      "server_activated": "{{server}} activated",
      "device_approved": "Device code approved for {{server}}",
      "key_reissued": "New activation key issued for {{server}}",
      "key_applied": "{{server}} switched to the new key",
      "plan_changed": "Plan on {{server}} changed from {{from}} to {{to}}",
      "plan_change_scheduled": "{{server}} scheduled to change to {{to}} on {{date}}",
      "channel_changed": "Update channel on {{server}} set to {{channel}}",
      "core_updated": "bPanel on {{server}} updated from {{from}} to {{to}}",
      "transfer_started": "Transfer of {{server}} to {{to}} started",
      "old_installation_deactivated": "Old installation on {{server}} stopped",
      "cancel_scheduled": "Subscription for {{server}} cancelled from {{date}}",
      "cancel_reverted": "Cancellation of {{server}} undone",
      "server_removed": "{{server}} removed",
      "trial_started": "Free trial started",
      "payment_succeeded": "Payment for {{server}} received",
      "payment_failed": "Payment for {{server}} failed",
      "past_due_started": "The renewal for {{server}} didn't go through; the payment grace started",
      "server_locked": "The subscription for {{server}} ended and its control panel locked",
      "server_renewed": "{{server}} renewed on the {{plan}} plan",
      "server_unlocked": "The control panel on {{server}} unlocked"
    }
  },
  "firstPayment": {
    "title": "Pay the first period before the install",
    "body": "{{host}} is ready to install after its first payment. Once it's paid, the install steps and activation key appear.",
    "pay": "Pay first period",
    "askBilling": "Ask the owner, an admin or a billing member to pay the first period.",
    "note": "The subscription starts on the day you pay. If you no longer need the server before paying, you can remove it in settings at no cost."
  }
}
